'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useTheme } from '../theme/provider'

export interface Story {
  id: string | number
  title: string
  image: string
  href: string
  isNew?: boolean
}

export interface StoriesProps {
  stories: Story[]
  className?: string
  /**
   * Title for the stories section
   */
  sectionTitle?: string
}

/**
 * Stories Component - Instagram/Facebook style stories
 * Based on Real Madrid website stories section
 *
 * Features:
 * - Horizontal scrollable story circles
 * - Gradient ring for new/unviewed stories
 * - Story title below circle
 * - Smooth scroll behavior
 * - Theme-aware colors
 *
 * @example
 * ```tsx
 * <Stories
 *   stories={[
 *     { id: 1, title: 'Match Day', image: '/story1.jpg', href: '/stories/1', isNew: true },
 *     { id: 2, title: 'Training', image: '/story2.jpg', href: '/stories/2' }
 *   ]}
 * />
 * ```
 */
export function Stories({
  stories = [],
  className = '',
  sectionTitle,
}: StoriesProps) {
  const { theme } = useTheme()

  if (!stories || stories.length === 0) {
    return null
  }

  return (
    <section className={`mb-8 ${className}`}>
      {sectionTitle && (
        <h3
          className="text-lg font-bold mb-4"
          style={{ color: theme.colors.text }}
        >
          {sectionTitle}
        </h3>
      )}

      {/* Horizontal Scrollable Container */}
      <div className="relative">
        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
          {stories.map((story) => (
            <Link
              key={story.id}
              href={story.href}
              className="flex-shrink-0 group"
            >
              <div className="flex flex-col items-center gap-2 w-20">
                {/* Story Circle with Gradient Ring */}
                <div className="relative">
                  {/* Gradient Ring (shown if story is new) */}
                  {story.isNew && (
                    <div
                      className="absolute inset-0 rounded-full p-[3px]"
                      style={{
                        background: `linear-gradient(45deg, ${theme.colors.primary}, ${theme.colors.secondary || theme.colors.primary})`,
                      }}
                    >
                      <div
                        className="w-full h-full rounded-full"
                        style={{ backgroundColor: theme.colors.background }}
                      />
                    </div>
                  )}

                  {/* Story Image */}
                  <div
                    className={`relative ${story.isNew ? 'p-[5px]' : ''}`}
                  >
                    <div className="relative w-16 h-16 rounded-full overflow-hidden ring-2 ring-offset-2 transition-transform duration-300 group-hover:scale-110"
                      style={{
                        borderColor: story.isNew ? 'transparent' : theme.colors.border,
                      }}
                    >
                      <Image
                        src={story.image}
                        alt={story.title}
                        fill
                        className="object-cover"
                        sizes="64px"
                      />
                    </div>
                  </div>
                </div>

                {/* Story Title */}
                <span
                  className="text-xs text-center line-clamp-2 leading-tight font-medium"
                  style={{ color: theme.colors.textSecondary }}
                >
                  {story.title}
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* Scroll Gradient Overlays (optional visual enhancement) */}
        <div
          className="absolute top-0 left-0 bottom-0 w-8 pointer-events-none"
          style={{
            background: `linear-gradient(to right, ${theme.colors.background}, transparent)`,
          }}
        />
        <div
          className="absolute top-0 right-0 bottom-0 w-8 pointer-events-none"
          style={{
            background: `linear-gradient(to left, ${theme.colors.background}, transparent)`,
          }}
        />
      </div>

      {/* Custom CSS for hiding scrollbar */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  )
}
